import {useCallback, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";

// Asked on Submit when a file came back from its upload-time check with warnings: the entry may
// still go in, but only once the contributor has seen them and chosen to.
//
// Answers [confirmWarnings, dialog]. confirmWarnings(warnings) resolves true straight away when
// there is nothing to warn about, otherwise once the contributor answers; render `dialog`
// somewhere in the form's component.
//
// Drawn by React rather than through Bootstrap's modal JS, and portalled to the body, so it also
// works from inside the Redo modal, which is itself a Bootstrap modal.
export default function useUploadQaWarningsConfirm() {
	const [warnings, setWarnings] = useState(null);
	const resolveRef = useRef(null);

	const confirmWarnings = useCallback((pendingWarnings) => {
		if (pendingWarnings.length === 0) {
			return Promise.resolve(true);
		}
		return new Promise(resolve => {
			// Only one question at a time: an earlier one still open counts as a Go back.
			resolveRef.current?.(false);
			resolveRef.current = resolve;
			setWarnings(pendingWarnings);
		});
	}, []);

	const answer = useCallback((submit) => {
		resolveRef.current?.(submit);
		resolveRef.current = null;
		setWarnings(null);
	}, []);

	// A form that goes away mid-question must not leave its submit waiting forever.
	useEffect(() => () => resolveRef.current?.(false), []);

	const dialog = warnings && createPortal(<UploadQaWarningsDialog warnings={warnings} answer={answer} />, document.body);

	return [confirmWarnings, dialog];
}

function UploadQaWarningsDialog({warnings, answer}) {
	const goBackRef = useRef(null);

	useEffect(() => {
		goBackRef.current?.focus();

		const onKeyDown = (event) => {
			if (event.key === "Escape") {
				answer(false);
			}
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [answer]);

	// Above a Bootstrap modal (1055) and its backdrop (1050), for when this opens from the Redo modal.
	return (
		<>
			<div className="modal-backdrop show" style={{zIndex: 1060}} />
			<div className="modal d-block" style={{zIndex: 1065}} tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="uploadQaWarningsTitle" onClick={event => {
				if (event.target === event.currentTarget) {
					answer(false);
				}
			}}>
				<div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
					<div className="modal-content">
						<div className="modal-header">
							<h5 className="modal-title" id="uploadQaWarningsTitle">
								<i className="bi bi-exclamation-triangle-fill text-warning me-2" />
								Some files have QA warnings
							</h5>
							<button type="button" className="btn-close" aria-label="Close" onClick={() => answer(false)} />
						</div>
						<div className="modal-body">
							<p>You can still submit, but please check these first:</p>
							<ul className="mb-0">
								{warnings.map((warning, index) => <li key={index}>{warning}</li>)}
							</ul>
						</div>
						<div className="modal-footer">
							<button ref={goBackRef} type="button" className="btn btn-secondary" onClick={() => answer(false)}>Go back</button>
							<button type="button" className="btn btn-warning" onClick={() => answer(true)}>Submit anyway</button>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
